import { baseApi } from '@krp/client-runtime'

import type { AccessMatrix, MyAccess } from '../model/types'
import { decodeAccessMatrix, decodeMyAccess, decodeRoleDefaults, decodeRoleResources } from './contracts'
import { PLATFORM_ACCESS_URLS } from './urls'

/**
 * Права Platform в общем `baseApi`. «Мои права» нужны экранам, чтобы
 * отражать сервер (прятать недоступное с причиной); решение всё равно за
 * сервером на каждой команде.
 */
export const PLATFORM_ACCESS_TAG = 'PlatformAccess' as const

export interface PlatformRoleArgs {
  role: string
}

export interface PlatformSaveRoleResourcesArgs {
  role: string
  resourceIds: readonly string[]
}

export interface PlatformSaveRolePermissionsArgs {
  role: string
  permissions: readonly string[]
}

export const accessApi = baseApi.enhanceEndpoints({ addTagTypes: [PLATFORM_ACCESS_TAG] }).injectEndpoints({
  endpoints: (build) => ({
    platformMyAccess: build.query<MyAccess, void>({
      query: () => ({ url: PLATFORM_ACCESS_URLS.mine, decode: decodeMyAccess }),
      providesTags: [{ type: PLATFORM_ACCESS_TAG, id: 'MINE' }],
    }),
    platformAccessMatrix: build.query<AccessMatrix, void>({
      query: () => ({ url: PLATFORM_ACCESS_URLS.matrix, decode: decodeAccessMatrix }),
      providesTags: [{ type: PLATFORM_ACCESS_TAG, id: 'MATRIX' }],
    }),
    platformRoleResources: build.query<readonly string[], PlatformRoleArgs>({
      query: ({ role }) => ({ url: PLATFORM_ACCESS_URLS.roleResources(role), decode: decodeRoleResources }),
      providesTags: (_result, _error, { role }) => [{ type: PLATFORM_ACCESS_TAG, id: `ROLE:${role}:RESOURCES` }],
    }),
    /** Стартовый набор прав роли (RP-FB5): только подсказка для галочек, матрицу не меняет. */
    platformRoleDefaults: build.query<readonly string[], PlatformRoleArgs>({
      query: ({ role }) => ({ url: PLATFORM_ACCESS_URLS.roleDefaults(role), decode: decodeRoleDefaults }),
    }),
    /** Заменить допуск роли целиком (PUT). 409 — допуск изменили параллельно: перечитать. */
    platformSaveRoleResources: build.mutation<null, PlatformSaveRoleResourcesArgs>({
      query: ({ role, resourceIds }) => ({
        url: PLATFORM_ACCESS_URLS.roleResources(role),
        method: 'PUT',
        body: [...resourceIds],
      }),
      invalidatesTags: (_result, _error, { role }) => [
        { type: PLATFORM_ACCESS_TAG, id: `ROLE:${role}:RESOURCES` },
        { type: PLATFORM_ACCESS_TAG, id: 'MINE' },
      ],
    }),
    /** Заменить права роли в матрице целиком (PUT пакета Kalmykov Access). */
    platformSaveRolePermissions: build.mutation<null, PlatformSaveRolePermissionsArgs>({
      query: ({ role, permissions }) => ({
        url: PLATFORM_ACCESS_URLS.rolePermissions(role),
        method: 'PUT',
        body: { permissions: [...permissions] },
      }),
      invalidatesTags: [
        { type: PLATFORM_ACCESS_TAG, id: 'MATRIX' },
        { type: PLATFORM_ACCESS_TAG, id: 'MINE' },
      ],
    }),
  }),
})

export const {
  platformMyAccess,
  platformAccessMatrix,
  platformRoleResources,
  platformRoleDefaults,
  platformSaveRoleResources,
  platformSaveRolePermissions,
} = accessApi.endpoints
